import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ source = "website", avatar, pathway, interest, withMessage = false, cta = "Send", onSuccess, compact = false, topics }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [topicKey, setTopicKey] = useState(topics?.[0]?.key ?? "");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const activeTopic = topics?.find((t) => t.key === topicKey);
  const effectiveSource = activeTopic?.source ?? source;
  const effectiveAvatar = activeTopic?.avatar ?? avatar;
  const effectiveCta = activeTopic?.cta ?? cta;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError("Please enter your name and a valid email."); return; }
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/lead`, { name, email, source: effectiveSource, avatar: effectiveAvatar, pathway, interest, message: message || null });
      setDone(true);
      onSuccess?.();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="lead-success" className="text-center py-6">
        <div className="w-14 h-14 rounded-full btn-gold flex items-center justify-center mx-auto mb-4"><Check size={26} className="text-[#14100A]" /></div>
        <p className="font-display text-2xl text-cream mb-2">You're in.</p>
        <p className="text-cream/60">Look for a note from us shortly. The work is glad you're here.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className={compact ? "space-y-3" : "space-y-4"}>
      {topics && (
        <>
        <label htmlFor="lead-topic" className="sr-only">What is this about?</label>
        <select id="lead-topic" data-testid="lead-topic" value={topicKey} onChange={(e) => setTopicKey(e.target.value)}
          className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-[#C9A24B]/25 text-cream text-lg outline-none focus:border-[#C9A24B] transition-colors">
          {topics.map((t) => (
            <option key={t.key} value={t.key} className="text-[#14100A]">{t.label}</option>
          ))}
        </select>
        </>
      )}
      <label htmlFor="lead-name" className="sr-only">Your name</label>
      <input id="lead-name" data-testid="lead-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name"
        className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-[#C9A24B]/25 text-cream text-lg outline-none focus:border-[#C9A24B] transition-colors" />
      <label htmlFor="lead-email" className="sr-only">Your email address</label>
      <input id="lead-email" data-testid="lead-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" autoComplete="email"
        className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-[#C9A24B]/25 text-cream text-lg outline-none focus:border-[#C9A24B] transition-colors" />
      {withMessage && (
        <><label htmlFor="lead-message" className="sr-only">Tell us a little about what you have in mind</label>
        <textarea id="lead-message" data-testid="lead-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us a little about what you have in mind" rows={4}
          className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-[#C9A24B]/25 text-cream text-base outline-none focus:border-[#C9A24B] transition-colors resize-none" /></>
      )}
      {error && <p role="alert" className="text-red-400 text-sm">{error}</p>}
      <button data-testid="lead-submit" type="submit" disabled={loading}
        className="btn-gold w-full py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-70">
        {loading ? <><Loader2 size={20} className="animate-spin" /> Sending…</> : <>{effectiveCta} <ArrowRight size={20} /></>}
      </button>
      <p className="text-xs text-cream/35 leading-relaxed">
        By submitting you consent to receive email and SMS from Gary Malkin Enterprises. Message and data rates may apply. Reply STOP to opt out.
      </p>
    </form>
  );
};

export const LeadModal = ({ open, onClose, title, subtitle, source, avatar, pathway, interest, withMessage, cta }) => (
  <AnimatePresence>
    {open && (
      <motion.div data-testid="lead-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md" onClick={onClose}>
        <motion.div initial={{ scale: 0.95, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-lg glass rounded-3xl p-7 md:p-10 bg-[#0B1020]/95" onClick={(e) => e.stopPropagation()}>
          <button data-testid="lead-modal-close" onClick={onClose} className="absolute top-5 right-5 text-cream/60 hover:text-[#E7CC86] transition-colors"><X size={24} /></button>
          <p className="text-sm tracking-[0.22em] uppercase text-[#E7CC86]/80 mb-3">{title}</p>
          {subtitle && <h3 className="font-display font-semibold text-2xl md:text-3xl text-cream mb-6 leading-tight">{subtitle}</h3>}
          <LeadForm source={source} avatar={avatar} pathway={pathway} interest={interest} withMessage={withMessage} cta={cta} />
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
